import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Separator, Typography } from '@heroui/react';
import type { ShiftLedgerSummary } from '@/api/models';
import { formatGel, getNodeTestIdProp } from '@/utils';
import { formatShiftTime, SHIFT_STATUS_META } from '../../shiftsMeta';
import { ShiftLedgerAccountsTable } from './ShiftLedgerAccountsTable';

type ShiftLedgerSummaryBodyProps = {
    summary: ShiftLedgerSummary;
};

/**
 * One shift's figures (render-only): who ran it and when, its cash count, what
 * it posted per account, and the receipts the totals leave out.
 */
export const ShiftLedgerSummaryBody: React.FC<ShiftLedgerSummaryBodyProps> = ({
    summary,
}) => {
    const { t } = useTranslation('pages');
    const { shift } = summary;
    const status = SHIFT_STATUS_META[shift.status];

    const figure = (label: string, value: number | null, testId: string) => (
        <div className="flex items-center justify-between">
            <Typography color="muted" type="body-sm">
                {label}
            </Typography>
            <Typography
                className="tabular-nums"
                type="body-sm"
                {...getNodeTestIdProp(testId)}
            >
                {value === null ? '—' : formatGel(value)}
            </Typography>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-col">
                    <Typography type="body" weight="semibold">
                        {shift.openedByName ?? t('shifts.unknownSeller')}
                    </Typography>
                    <Typography color="muted" type="body-xs">
                        {`${formatShiftTime(shift.openedAt)} → ${formatShiftTime(shift.closedAt)}`}
                    </Typography>
                </div>
                <Chip color={status.color} size="sm" variant="soft">
                    {t(status.labelKey)}
                </Chip>
            </div>

            <div className="flex flex-col gap-1.5">
                {figure(
                    t('shifts.summary.openingFloat'),
                    shift.openingFloat,
                    'shift-ledger-summary-opening',
                )}
                {figure(
                    t('shifts.summary.expected'),
                    shift.expectedAtClose,
                    'shift-ledger-summary-expected',
                )}
                {figure(
                    t('shifts.summary.counted'),
                    shift.closingCount,
                    'shift-ledger-summary-counted',
                )}
                {figure(
                    t('shifts.summary.difference'),
                    shift.closeDiscrepancy,
                    'shift-ledger-summary-difference',
                )}
                <div className="flex items-center justify-between">
                    <Typography color="muted" type="body-sm">
                        {t('shifts.summary.receipts')}
                    </Typography>
                    <Typography className="tabular-nums" type="body-sm">
                        {shift.receiptCount}
                    </Typography>
                </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
                <Typography type="body-sm" weight="semibold">
                    {t('shifts.summary.accountsTitle')}
                </Typography>
                {summary.accounts.length === 0 ? (
                    <Typography
                        color="muted"
                        type="body-sm"
                        {...getNodeTestIdProp(
                            'shift-ledger-summary-no-accounts',
                        )}
                    >
                        {t('shifts.summary.noAccounts')}
                    </Typography>
                ) : (
                    <ShiftLedgerAccountsTable summary={summary} />
                )}
            </div>

            <div className="flex flex-col gap-0.5">
                <Typography color="muted" type="body-xs">
                    {t('shifts.summary.included', {
                        count: summary.receiptsIncluded,
                    })}
                </Typography>
                {summary.receiptsReversed > 0 ? (
                    <Typography color="muted" type="body-xs">
                        {t('shifts.summary.reversed', {
                            count: summary.receiptsReversed,
                        })}
                    </Typography>
                ) : null}
                {summary.receiptsUnposted > 0 ? (
                    // Posting runs inside the sale, so this should never show:
                    // when it does, it is a data finding, not a footnote.
                    <Chip
                        className="self-start"
                        color="danger"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp('shift-ledger-summary-unposted')}
                    >
                        {t('shifts.summary.unposted', {
                            count: summary.receiptsUnposted,
                        })}
                    </Chip>
                ) : null}
            </div>
        </div>
    );
};
